// The STAFF half of assisted completion: a Phoenix staff member driving an
// applicant's form, in the same sitting.
//
// Decision 18 is full parity — this is the SAME surface a broker gets, with the
// same certification control and the same copy, over a different transport.
// There is deliberately no staff-only variant of the form or of the questions.

import { useMemo } from "react";
import { Navigate, useParams } from "react-router-dom";
import { ApiError } from "@/api/client";
import { prequalificationApi } from "@/api/prequalification";
import { useAuth } from "@/hooks/useAuth";
import { AssistedPrequalSurface } from "@/portal/pages/prequalify/AssistedPrequalSurface";
import { DealExplorerPage } from "@/portal/pages/prequalify/DealExplorerPage";
import { PrequalResultPage } from "@/portal/pages/prequalify/PrequalResultPage";
import type { PrequalRole } from "@/portal/api/prequalification";
import type { AssistedDraftTransport } from "@/portal/pages/prequalify/useAssistedPrequalDraftAdapter";
import { PortalAuthProvider } from "@/portal/PortalAuthContext";

const BACK_TO = "/deal-flow/prequalification";

export function PrequalificationAssistPage() {
  const { draftId } = useParams();
  // The STAFF session, deliberately — the PortalAuthProvider below settles at
  // `me = null` for a staff principal, so the shared surface cannot resolve a
  // name for itself and takes one as a prop.
  const { user } = useAuth();

  const transport = useMemo<AssistedDraftTransport>(
    () => ({
      load: (id) => prequalificationApi.assistLoad(id),
      step: (id, body) => prequalificationApi.assistStep(id, body),
      finalize: (id, body) => prequalificationApi.assistFinalize(id, body),
      // A conflict is a real answer the form renders as "reload and try again",
      // so it has to survive the trip out of the HTTP client intact.
      isConflict: (error) => error instanceof ApiError && error.status === 409,
    }),
    [],
  );

  if (!draftId) return <Navigate to={BACK_TO} replace />;

  // The funnel's own chrome (PortalShell) reads the portal session context, and
  // this route renders inside the STAFF app where that context does not exist —
  // without the provider the shared component throws on mount.
  //
  // It is safe and it is not a session: the provider probes /api/portal/auth/me
  // once, that probe 401s for a staff principal, and it settles on `me = null`
  // exactly as it does for an anonymous visitor on the public funnel. It never
  // redirects from that state, and it grants nothing — every request this page
  // makes goes to the staff API under the staff cookie.
  //
  // The chrome that results is the PUBLIC funnel header rather than the staff
  // shell, which is right rather than a compromise: the same-sitting mechanism
  // is handing the client the laptop, and they should see an application rather
  // than an internal work queue — skinned to WHOEVER'S SITE the draft was
  // started on. A draft started from a partner's prequalification site renders
  // that partner's mark and colours here, exactly as its public funnel does;
  // only a Phoenix-run application shows Phoenix. The bar above the funnel is
  // the one part that stays Phoenix-neutral, because it is the operator's
  // tooling and not something the client should act on.
  return (
    <PortalAuthProvider>
      <AssistedPrequalSurface
        draftId={draftId}
        transport={transport}
        backTo={BACK_TO}
        backLabel="Back to prequalification"
        // Display only — see the portal twin. The recorded certifier comes from
        // the authenticated session server-side, never from a body.
        operatorName={user?.displayName}
      />
    </PortalAuthProvider>
  );
}

/**
 * Where the shared form navigates itself after FINALIZE.
 *
 * The same result screen the applicant would see — reference number, validity,
 * the documents to hand over — wrapped in the same portal context for the same
 * reason as the surface above. It reads what the form wrote to this tab, which
 * is why it has to render in the app the form was running in.
 */
export function PrequalificationAssistResultPage({ role }: { role: PrequalRole }) {
  return (
    <PortalAuthProvider>
      {/* This namespace has no funnel route — the assisted form is addressed by
          draft id, which this screen cannot reconstruct. So the no-state
          redirect lands on the staff list rather than on a 404, and "Adjust my
          answers" does not render at all. See PrequalResultPageProps.homeHref. */}
      <PrequalResultPage role={role} homeHref={BACK_TO} />
    </PortalAuthProvider>
  );
}

/**
 * The Deal Explorer, reachable from the assisted RESULT screen.
 *
 * The result screen's buyer "Model a deal" card links to `{base}/explore`,
 * derived from the first `/prequalify` segment of the current path — so on the
 * staff side it resolves to `/deal-flow/prequalify/explore`. The public funnel
 * registers that route on both its native and its partner mount; this namespace
 * did not, so the card was a 404 for staff and for staff alone. Every public
 * path was always fine.
 *
 * Wrapped for the SAME reason the two screens above are, and it is not
 * optional: the Explorer renders `PortalShell`, `PortalShell` calls
 * `usePortalAuth`, and that hook THROWS outside a provider. Without this the
 * route would resolve and then blow up on mount — a worse failure than the 404
 * it replaces.
 *
 * `homeHref` is the second half: the Explorer's own breadcrumb points back into
 * the funnel, which is another route this namespace does not have.
 */
export function PrequalificationAssistExplorerPage() {
  return (
    <PortalAuthProvider>
      <DealExplorerPage homeHref={BACK_TO} />
    </PortalAuthProvider>
  );
}
